import {useMemo} from 'react'
import {useLocation} from 'react-router-dom'
import type {AppLocale} from './locales'
import {DEFAULT_LOCALE, isAppLocale, SUPPORTED_LOCALES} from './locales'

export {DEFAULT_LOCALE, isAppLocale, SUPPORTED_LOCALES}

export function pathSegments(pathname: string): string[] {
  return pathname.split('/').filter(Boolean)
}

export function stripLocaleFromSegments(segments: string[]): string[] {
  if (segments[0] && isAppLocale(segments[0])) {
    return segments.slice(1)
  }
  return segments
}

/** Path after `/:locale` (e.g. `/en/login` → `/login`). */
export function appPathAfterLocale(pathname: string): string {
  const segs = stripLocaleFromSegments(pathSegments(pathname))
  if (!segs.length) return '/'
  return `/${segs.join('/')}`
}

/** Prefix app path with locale (`/bg/login`). */
export function withLocalePath(locale: AppLocale, path: string): string {
  if (!path) return `/${locale}`
  if (
    path.startsWith('http://') ||
    path.startsWith('https://') ||
    path.startsWith('//') ||
    path.startsWith('mailto:') ||
    path.startsWith('tel:')
  ) {
    return path
  }
  const normalized = path.startsWith('/') ? path : `/${path}`
  const segs = pathSegments(normalized)
  if (segs[0] && isAppLocale(segs[0])) {
    return (
      `/${locale}/${segs.slice(1).join('/')}` +
      (normalized.endsWith('/') && segs.length > 1 ? '/' : '')
    )
  }
  if (normalized === '/') {
    return `/${locale}`
  }
  return `/${locale}${normalized}`
}

/**
 * Synthetic location for nested `<Routes>`: match patterns like `login` while the
 * browser URL stays `/en/login`.
 */
export function useRoutesLocationUnderLocale() {
  const location = useLocation()
  return useMemo(
    () => ({
      ...location,
      pathname: appPathAfterLocale(location.pathname),
    }),
    [location],
  )
}

export function segmentAfter(
  segments: string[],
  marker: string,
): string | undefined {
  const i = segments.indexOf(marker)
  if (i < 0) {
    return undefined
  }
  return segments[i + 1]
}
